import React, { ReactNode, useState } from 'react';
import { Button, Typography, FormControl, RadioGroup, FormControlLabel, Radio, TextField, CircularProgress, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SnackbarAlert, { Severity } from '../alert/SnackbarAlert';
import TopNavActions from './TopNavActions';
import { useApiService } from "../../services/ApiServiceProvider";

interface EnrollmentDeclinedViewProps {
  enrollment: any;
  onPageChange: (page: string) => void;
}

const reasonsList = [
  'I am not interested in having my voice recorded',
  'I do not have enough time today and want to be contacted later',
  'I do not have enough time and do not want to be contacted later',
  'I prefer not to share any health information for research',
  'Other'
];

const EnrollmentDeclinedView: React.FC<EnrollmentDeclinedViewProps> = ({ enrollment, onPageChange }) => {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [selectedDeclineReason, setSelectedDeclineReason] = useState<string>('');
  const [declineReasonSpecified, setDeclineReasonSpecified] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [snackbarContent, setSnackbarContent] = useState<ReactNode>();
  const [snackbarSeverity, setSnackbarSeverity] = useState<Severity>('info');
  const apiService = useApiService();
  const { t } = useTranslation('enrollment');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (title: string, content: ReactNode, severity: Severity) => {
    setSnackbarTitle(title);
    setSnackbarContent(content);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    // Close snackbar after 5 seconds
    setTimeout(() => {
      handleSnackbarClose();
    }, 5000);
  };


  const handleReasonChange = (reason: string) => {
    setSelectedDeclineReason(reason);
    if (reason !== 'Other') {
      setDeclineReasonSpecified('');
    }
  };

  const handleFinish = async () => {
    enrollment.declineReason = selectedDeclineReason;
    enrollment.declineReasonSpecified = declineReasonSpecified;
    enrollment.enrolled = false;
    enrollment.completedAt = new Date();
    setIsLoadingData(true);
    setDisplayError(false);
    setSnackbarOpen(false);

    try {
      await apiService.enrollmentCreate(enrollment);
      onPageChange('finish');
    } catch (error: Error | any) {
      console.log(error);
      setIsLoadingData(false);
      setDisplayError(true);
      showSnackbar(
          'Error',
          error?.message || 'An error occurred while submitting. Please try again.',
          'error'
        );
    }
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const isFinishDisabled =
    !selectedDeclineReason ||
    (selectedDeclineReason === 'Other' && declineReasonSpecified.trim() === '');

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        disabled={isLoadingData}
        hideExit={true}
      />

      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1}>{t("Registration declined")}</Typography>
      <Typography variant="h5" component='h2' align="center" sx={{mt: 4, mb: 4}}>{t("Thank you for your time")}</Typography>


      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend">
          <Typography variant="h6" component='h3'>{t("Please select a reason for declining")}</Typography>
        </FormLabel>
        <RadioGroup
          name="declineReason"
          value={selectedDeclineReason}
          onChange={e => handleReasonChange(e.target.value)}
        >
          {reasonsList.map(reason => (
            <FormControlLabel
              key={reason}
              value={reason}
              control={<Radio />}
              label={t(reason)}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {selectedDeclineReason === 'Other' && (
        <FormControl fullWidth>
          <TextField
            label={t("Please specify")}
            id="decline-reason-specified"
            value={declineReasonSpecified}
            onChange={e => setDeclineReasonSpecified(e.target.value)}
            margin='normal'
          />
        </FormControl>
      )}

      {/* Show CircularProgress before the action button */}
      {isLoadingData && (
        <div style={{ minHeight: '4rem', textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {displayError && (
        <SnackbarAlert
          open={snackbarOpen}
          title={snackbarTitle}
          severity={snackbarSeverity}
          content={snackbarContent}
          vertical='top'
          onClose={handleSnackbarClose}
        />
      )}

      <Button variant="contained" onClick={handleFinish} disabled={isFinishDisabled || isLoadingData} style={{ marginTop: 32, marginBottom: 32 }}>
        {t("Finish")}
      </Button>
    </React.Fragment>
  );
};

export default EnrollmentDeclinedView;
